import React, { useContext, useState, useEffect } from "react";
import { Sidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import { Link, useLocation } from "react-router-dom";
import styles from "../Styles/SideNav.module.css";
import logo from "../Assets/logo/logo.svg";

import { FaBox, FaUsers, FaWallet } from "react-icons/fa";
import { MdDashboard, MdShoppingCart } from "react-icons/md";
import { BsPin } from "react-icons/bs";
import { AiOutlineUser } from "react-icons/ai";
import { FiLogOut, FiMenu, FiSettings } from "react-icons/fi";
import UserContext from "../context/UserContext";
import useLogout from "../hooks/useLogout";

function SideNav() {
  const { user } = useContext(UserContext);
  const location = useLocation();
  const handleLogout = useLogout();
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const handleMediaChange = (e) => {
      setCollapsed(e.matches);
    };
    
    // Set initially
    setCollapsed(mediaQuery.matches);
    
    mediaQuery.addEventListener("change", handleMediaChange);
    return () => mediaQuery.removeEventListener("change", handleMediaChange);
  }, []);

  // Menu item styles with active state handling
  const menuItemStyles = {
    root: {
      fontSize: "15px",
      fontWeight: 400,
    },
    button: ({ active, disabled }) => ({
      backgroundColor: active ? "#f4f7ff" : "transparent",
      color: active ? "#1814f3" : "#555",
      fontWeight: active ? 500 : 300,
      "&:hover": {
        backgroundColor: active ? "#f4f7ff" : "#f5f5f5",
        color: "#1814f3",
      },
      paddingLeft: "16px",
      paddingRight: "16px",
    }),
    icon: ({ active }) => ({
      color: active ? "#1814f3" : "#757575",
      fontSize: "20px",
    }),
    subMenuContent: {
      backgroundColor: "#fafafa",
    },
    label: ({ disabled }) => ({
      opacity: disabled ? 0.5 : 1,
    }),
  };

  // Common menu items for both user types
  const renderCommonMenuItems = () => (
    <>
      <MenuItem
        icon={<MdDashboard />}
        component={<Link to="/overview" />}
        active={location.pathname === "/overview"}
      >
        Overview
      </MenuItem>

      <SubMenu icon={<MdShoppingCart />} label="Orders">
        <MenuItem
          component={<Link to="/manual/history" />}
          active={location.pathname === "/manual/history"}
        >
          History
        </MenuItem>
        <MenuItem
          component={<Link to="/manual/Transactions" />}
          active={location.pathname === "/manual/Transactions"}
        >
          Transactions
        </MenuItem>
      </SubMenu>

      <SubMenu icon={<FaBox />} label="Products">
        <MenuItem
          component={<Link to="/product/list" />}
          active={location.pathname === "/product/list"}
        >
          List
        </MenuItem>
        <MenuItem
          component={<Link to="/product/create" />}
          active={location.pathname === "/product/create"}
        >
          Create
        </MenuItem>
      </SubMenu>
    </>
  );

  const renderPINMenu = () => (
    <SubMenu icon={<BsPin />} label="PINs">
      <MenuItem
        component={<Link to="/pin/list" />}
        active={location.pathname === "/pin/list"}
      >
        List
      </MenuItem>
      <MenuItem
        component={<Link to="/pin/create" />}
        active={location.pathname === "/pin/create"}
      >
        Create
      </MenuItem>
      <MenuItem
        component={<Link to="/pin/mark-as-used" />}
        active={location.pathname === "/pin/mark-as-used"}
      >
        Mark as Used
      </MenuItem>
      <SubMenu label="PIN Type">
        <MenuItem
          component={<Link to="/pin/type/price" />}
          active={location.pathname === "/pin/type/price"}
        >
          Type Price
        </MenuItem>
        <MenuItem
          component={<Link to="/pin/type/list" />}
          active={location.pathname === "/pin/type/list"}
        >
          Type List
        </MenuItem>
      </SubMenu>
      <SubMenu label="PIN Export">
        <MenuItem
          component={<Link to="/pin/export" />}
          active={location.pathname === "/pin/export"}
        >
          Export
        </MenuItem>
        <MenuItem
          component={<Link to="/pin/export/list" />}
          active={location.pathname === "/pin/export/list"}
        >
          Exported-PINs
        </MenuItem>
      </SubMenu>
    </SubMenu>
  );

  const renderUsersMenu = (isSuperAdmin) => (
    <SubMenu icon={<FaUsers />} label="Users">
      <MenuItem
        component={<Link to="/users/distributors" />}
        active={location.pathname === "/users/distributors"}
      >
        Distributors
      </MenuItem>
      {isSuperAdmin && (
        <MenuItem
          component={<Link to="/users/admins" />}
          active={location.pathname === "/users/admins"}
        >
          Admins
        </MenuItem>
      )}
      <MenuItem
        component={<Link to="/users/fixedagents" />}
        active={location.pathname === "/users/fixedagents"}
      >
        Fixed Agents
      </MenuItem>
      <MenuItem
        component={<Link to="/users/customers" />}
        active={location.pathname === "/users/customers"}
      >
        Customers
      </MenuItem>
      <MenuItem
        component={
          <Link
            to={isSuperAdmin ? "/users/create" : "/users/create/distributor"}
          />
        }
        active={
          location.pathname ===
          (isSuperAdmin ? "/users/create" : "/users/create/distributor")
        }
      >
        {isSuperAdmin ? "New User" : "New Distributor"}
      </MenuItem>
    </SubMenu>
  );

  const renderPINSalesMenu = () => (
    <SubMenu icon={<BsPin />} label="PIN Sales">
      <MenuItem
        component={<Link to="/pinsales/history" />}
        active={location.pathname === "/pinsales/history"}
      >
        History
      </MenuItem>
      <SubMenu label="Distributors">
        <MenuItem
          component={<Link to="/pinsales/distributor/sold" />}
          active={location.pathname === "/pinsales/distributor/sold"}
        >
          Sold-pins
        </MenuItem>
        <MenuItem
          component={<Link to="/pinsales/distributor/bought" />}
          active={location.pathname === "/pinsales/distributor/bought"}
        >
          Bought-pins
        </MenuItem>
        <MenuItem
          component={<Link to="/pinsales/distributor/exported" />}
          active={location.pathname === "/pinsales/distributor/exported"}
        >
          Exported-pins
        </MenuItem>
      </SubMenu>
      <SubMenu label="Fixed Agents">
        <MenuItem
          component={<Link to="/pinsales/fixedagent/sold" />}
          active={location.pathname === "/pinsales/fixedagent/sold"}
        >
          Sold-pins
        </MenuItem>
        <MenuItem
          component={<Link to="/pinsales/fixedagent/bought" />}
          active={location.pathname === "/pinsales/fixedagent/bought"}
        >
          Bought-pins
        </MenuItem>
        <MenuItem
          component={<Link to="/pinsales/fixedagent/exported" />}
          active={location.pathname === "/pinsales/fixedagent/exported"}
        >
          Exported-pins
        </MenuItem>
      </SubMenu>
    </SubMenu>
  );

  const renderWalletMenu = () => (
    <SubMenu icon={<FaWallet />} label="Wallet">
      <SubMenu label="Distributors">
        <MenuItem
          component={<Link to="/wallet/distributor/history" />}
          active={location.pathname === "/wallet/distributor/history"}
        >
          History
        </MenuItem>
        <MenuItem
          component={<Link to="/wallet/distributor/debit" />}
          active={location.pathname === "/wallet/distributor/debit"}
        >
          Debit
        </MenuItem>
      </SubMenu>
      <SubMenu label="Fixed Agents">
        <MenuItem
          component={<Link to="/wallet/fixedagent/credit" />}
          active={location.pathname === "/wallet/fixedagent/credit"}
        >
          Credit
        </MenuItem>
        <MenuItem
          component={<Link to="/wallet/fixedagent/debit" />}
          active={location.pathname === "/wallet/fixedagent/debit"}
        >
          Debit
        </MenuItem>
      </SubMenu>
    </SubMenu>
  );

  const renderCustomersMenu = () => (
    <SubMenu icon={<AiOutlineUser />} label="Customers">
      <MenuItem
        component={<Link to="/customers/import" />}
        active={location.pathname === "/customers/import"}
      >
        Import
      </MenuItem>
      <MenuItem
        component={<Link to="/customers/export" />}
        active={location.pathname === "/customers/export"}
      >
        Export
      </MenuItem>
    </SubMenu>
  );

  if (!user?.isAuth) return null;

  const isSuperAdmin = user.userAgentType === "SUPERADMIN";
  const isAdmin = user.userAgentType === "ADMIN";

  if (!isSuperAdmin && !isAdmin) return null;

  return (
    <div className={styles.sidenav}>
      <Sidebar
        collapsed={collapsed}
        width="240px"
        collapsedWidth="70px"
        backgroundColor="#ffffff"
        rootStyles={{
          border: "none",
          height: "100vh",
          position: "sticky",
          top: 0,
        }}
      >
        <div className={styles.sidebarContent}>
          {/* Logo Section */}
          <div className={styles.logoWrapper}>
            <div className={styles.logoContent}>
              <img src={logo} alt="Logo" className={styles.logo} />
              {!collapsed && <p className={styles.agentPro}>Agent Pro</p>}
            </div>
            <button
              className={styles.collapseBtn}
              onClick={() => setCollapsed(!collapsed)}
              title={collapsed ? "Expand" : "Collapse"}
            >
              <FiMenu />
            </button>
          </div>

          {/* Menu Section */}
          <Menu menuItemStyles={menuItemStyles}>
            {renderCommonMenuItems()}
            {isSuperAdmin && renderPINMenu()}
            {renderUsersMenu(isSuperAdmin)}
            {renderPINSalesMenu()}
            {renderWalletMenu()}
            {isSuperAdmin && renderCustomersMenu()}
            <MenuItem
              icon={<FiSettings />}
              component={<Link to="/settings" />}
              active={location.pathname === "/settings"}
            >
              Settings
            </MenuItem>
          </Menu>

          {/* Logout Section */}
          <div className={styles.logoutWrapper}>
            {collapsed ? (
              <button className={styles.mobile_logout}  onClick={handleLogout}><FiLogOut  /></button>
            ) : (
              <button className={styles.logout} onClick={handleLogout}>
                <FiLogOut />
                <span>Logout</span>
              </button>
            )}
          </div>
        </div>
      </Sidebar>
    </div>
  );
}

export default SideNav;
